import React, { useState } from 'react';
import { LayoutInternal } from '@/layout';
import { TitleFeatured } from '@/components/typography';
import WhitePaper from '@/components/elevation/WhitePaper/WhitePaper';
import Text from '../../../components/typography/Text/Text';
import { ActionElement, FormElement } from '@/features/forms/CrieSeuRoteiro/CrieSeuRoteiro.styles';
import { Stepper } from '@/components/stepper';
import { StepsType } from '@/components/stepper/Stepper';
import { Button } from '@/components/button';
import validationScheme, { FormCreateJourneyDataType } from './validationScheme';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCrieSeuRoteiroContext } from '@/features/forms/CrieSeuRoteiro/context';
import {
  FormAccomodation,
  FormAdditionalInformation,
  FormContactData,
  FormPassenger,
  FormTravelData,
} from '@/features/forms/CrieSeuRoteiro/components';
import { handleDisabled } from '@/features/forms/CrieSeuRoteiro/helpers';
import { FormLoader } from '@/features/forms/components';
import useFeedbackHandler from '@/features/forms/hooks/useFeedbackHandler';
import formPayloadParser, { FormPayloadDataType } from '@/features/forms/formPayloadParser';
import { FORMS_ENDPOINT } from '@/helpers/browser';
import FeedbackHandler from '@/features/forms/components/FeedbackHandler/FeedbackHandler';
import { trackFBPixel, trackGTMEvent, trackRDStation } from '@/utils/tracking';
import { useTranslations } from 'next-intl';

const CrieSeuRoteiro = () => {
  const t = useTranslations('FormCrieSeuRoteiro');
  const { data } = useCrieSeuRoteiroContext();
  const { page } = data;

  const breadcrumbs: BreadcrumbType = [{ text: page.title }];

  const [step, setStep] = useState(0);
  const steps: StepsType = [
    t('Dados da viagem'),
    t('Passageiros'),
    t('Acomodação'),
    t('Informações adicionais'),
    t('Dados contato'),
  ];

  const { feedback, submitting, submitted, submitRequestHandler } = useFeedbackHandler();

  const methods = useForm<FormCreateJourneyDataType>({
    resolver: zodResolver(validationScheme),
    defaultValues: {
      travelReason: '',
      travelPlannedDate: '',
      travelSpeakEng: 'Sim',
      travelLangPref: 'Inglês',
      travelDestinations: [],
      travelCitiesToVisit: [],
      travelExperiencies: [],
      travelBudget: '',
      travelPaxAdtNumber: '1',
      travelPaxChdNumber: '0',
      travelPaxInfNumber: '0',
      travelAccommodationCategory: '',
      travelAccommodationType: '',
      message: '',
      purchasedFlight: '',
      name: '',
      email: '',
      phone: '',
      subscribeNews: false,
      phoneData: {
        number: '',
        countryName: '',
        dialCode: '',
      },
    },
  });

  const { isValid, errors } = methods.formState;
  const { trigger, clearErrors } = methods;

  const handlePrev = (event: React.MouseEvent) => {
    event.preventDefault();
    setStep((prevState) => (prevState <= 0 ? 0 : prevState - 1));
  };

  const handleNext = async (event: React.MouseEvent) => {
    event.preventDefault();
    if (handleDisabled(methods, step)) {
      // Step needs this special conditions to proper display the erros feedback
      if (step === 0) {
        if (Object.keys(errors).length) {
          clearErrors([
            'travelReason',
            'travelPlannedDate',
            'travelDestinations',
            'travelExperiencies',
            'travelBudget',
            'purchasedFlight',
          ]);
          // wait the screen hide any input error before move on to give
          // the user an impression of validation passing
          await new Promise((resolve) => setTimeout(resolve, 600));
        }
      }
      setStep((prevState) => (prevState >= steps.length - 1 ? steps.length : prevState + 1));
    } else {
      await trigger([
        'travelReason',
        'travelPlannedDate',
        'travelDestinations',
        'travelExperiencies',
        'travelBudget',
        'purchasedFlight',
      ]);
    }

    return;
  };

  const onSubmit = async (data: FormCreateJourneyDataType) => {
    if (!isValid) {
      return;
    }

    const payload = {
      ...formPayloadParser({ ...data } as FormPayloadDataType),
      travelDestinations: data.travelDestinations.map(({ value }) => value) as any,
      travelCitiesToVisit: data.travelCitiesToVisit.map(({ value }) => value) as any,
      travelExperiencies: data.travelExperiencies.map(({ value }) => value) as any,
    };

    setStep(steps.length);
    submitRequestHandler(FORMS_ENDPOINT.form_create_your_journey, payload).then((done) => {
      if (done) {
        methods.reset();
        // =====================
        // FORM EVENT TRACKING
        // =====================
        trackRDStation('form-criar-roteiro', payload);
        trackGTMEvent({ eventCategory: 'Form', eventAction: 'submit', eventLabel: 'form-criar-roteiro' });
        trackFBPixel('form-criar-roteiro', 'Form Criar Roteiro');
      } else {
        setStep(steps.length - 1);
      }
    });
  };

  return (
    <LayoutInternal breadcrumbs={breadcrumbs} navbar="sticky" footer="sticky" showWhatsAppDropup={false}>
      <WhitePaper col={12}>
        <TitleFeatured as="h1" title={page.title} />
        <Text as="p" size="lg" align="center" weight="bold" dangerouslySetInnerHTML={{ __html: page.summary || '' }} />
        <Text as="p" size="base" align="center" dangerouslySetInnerHTML={{ __html: page.description || '' }} />
      </WhitePaper>
      <WhitePaper col={12}>
        <Stepper active={step} steps={steps} />
        <FormProvider {...methods}>
          <FormElement
            action="/proxy/action/form-crieseuroteiro"
            method="post"
            id="frmMonteSeuPacote"
            name="frmMonteSeuPacote"
            onSubmit={methods.handleSubmit(onSubmit)}
          >
            <FeedbackHandler {...feedback} />
            <input type="hidden" data-privacy="true" name="_privacy_policy" value="1" />
            {step === 0 && <FormTravelData />}
            {step === 1 && <FormPassenger />}
            {step === 2 && <FormAccomodation />}
            {step === 3 && <FormAdditionalInformation />}
            {step === 4 && <FormContactData />}
            {submitting && <FormLoader />}
            {/*added by: KTDEV-176*/}
            {submitted && <FormLoader />}
            {(!submitted && !submitting && (
              <ActionElement>
                {step > 0 && (
                  <Button type="button" size="base" appearance="link" onClick={handlePrev}>
                    {t('VOLTAR')}
                  </Button>
                )}

                {(step === steps.length - 1 && (
                  <Button type="submit" appearance="primary">
                    {t('ENVIAR SOLICITAÇÃO')}
                  </Button>
                )) || (
                  <Button
                    type="button"
                    appearance="primary"
                    // disabled={!handleDisabled(methods, step)}
                    arrow
                    onClick={handleNext}
                  >
                    {t('PRÓXIMO')}
                  </Button>
                )}
              </ActionElement>
            )) || (
              <>
                {/*changed by: KTDEV-176*/}
                {/*<Hr spacing="md" />*/}
                {/*<Text align="center">*/}
                {/*  <Button href="/" appearance="link">*/}
                {/*    voltar para Home*/}
                {/*  </Button>*/}
                {/*</Text>*/}
              </>
            )}
          </FormElement>
        </FormProvider>
      </WhitePaper>
    </LayoutInternal>
  );
};

export default CrieSeuRoteiro;
